import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Lock, Users as UsersIcon } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { PageHeader, UserAvatar } from "@/components/common/Bits";
import { Tag } from "@/components/common/StatusChip";

export default function Permissions() {
  const qc = useQueryClient();
  const { user: me, readOnly } = useAuth();
  const [showInactive, setShowInactive] = useState(false);
  const { data: catalog } = useQuery({ queryKey: ["permissions"], queryFn: () => api.get("/permissions").then((r) => r.data) });
  const { data: allUsers = [] } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users").then((r) => r.data) });
  const users = allUsers.filter((u) => showInactive || u.active);
  const total = catalog?.all.length || 0;

  const toggle = async (u, perm, on) => {
    const permissions = on ? [...u.permissions, perm] : u.permissions.filter((p) => p !== perm);
    qc.setQueryData(["users"], (old) => old?.map((x) => (x.id === u.id ? { ...x, permissions } : x)));
    try {
      await api.patch(`/users/${u.id}`, { permissions });
      qc.invalidateQueries({ queryKey: ["users"] });
      if (u.id === me.id) qc.invalidateQueries({ queryKey: ["me"] });
    } catch (e) { toast.error(errMsg(e)); qc.invalidateQueries({ queryKey: ["users"] }); }
  };

  return (
    <div data-testid="permissions-page">
      <PageHeader eyebrow="Access control" title="Permission matrix" description="One column per person, one row per capability. Tick a box to grant it — it applies on their very next click. No roles to manage: each person has exactly the access you see here."
        actions={<label className="flex items-center gap-2 text-xs text-muted-foreground"><Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="perm-show-inactive" /> Show deactivated users</label>} />
      <div className="p-4 md:p-6">
        <div className="surface overflow-x-auto">
          <table className="w-full border-collapse text-sm" data-testid="permission-matrix-table">
            <thead>
              <tr className="border-b border-border">
                <th className="sticky left-0 z-10 min-w-[260px] bg-card px-4 py-3 text-left align-bottom"><span className="eyebrow">Capability</span><div className="text-[11px] font-normal text-muted-foreground">{users.length} user{users.length === 1 ? "" : "s"} · {total} capabilities</div></th>
                {users.map((u) => (
                  <th key={u.id} className={`min-w-[132px] border-l border-border px-2 py-3 text-center align-top ${u.active ? "" : "opacity-50"}`} data-testid={`perm-user-col-${u.id}`}>
                    <div className="flex flex-col items-center gap-1">
                      <UserAvatar name={u.name} src={u.avatar_url} size="sm" />
                      <span className="max-w-[120px] truncate text-xs font-bold" title={u.email}>{u.name}{u.id === me.id ? " (you)" : ""}</span>
                      <span className="mono text-[10px] text-muted-foreground">{u.permissions.length}/{total}</span>
                      {!u.active ? <Tag tone="red">deactivated</Tag> : u.permissions.length === total ? <Tag tone="blue">full access</Tag> : u.view_only ? <Tag tone="neutral">view only</Tag> : <Tag tone="emerald">can change data</Tag>}
                    </div>
                  </th>
                ))}
                {users.length === 0 && <th className="px-4 py-3 text-left text-xs font-normal text-muted-foreground"><UsersIcon className="mr-1 inline h-3.5 w-3.5" /> No users to show</th>}
              </tr>
            </thead>
            <tbody>
              {catalog?.groups.map((g) => <GroupRows key={g.key} group={g} users={users} me={me} selfLocked={catalog.self_locked} toggle={toggle} readOnly={readOnly} />)}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-muted-foreground">
          <span><span className="eyebrow text-emerald-500">write</span> = can change data. Someone with no write capabilities is automatically view-only.</span>
          <span><Lock className="mr-1 inline h-3 w-3" /> You cannot remove your own user-management or matrix access, and one active person must always keep “Edit permission matrix”.</span>
        </div>
      </div>
    </div>
  );
}

function GroupRows({ group, users, me, selfLocked, toggle, readOnly }) {
  return (
    <>
      <tr className="border-y border-border bg-muted/40"><td colSpan={users.length + 1} className="sticky left-0 px-4 py-1.5"><span className="eyebrow">{group.label}</span></td></tr>
      {group.items.map((p) => (
        <tr key={p.key} className="row-hover border-b border-border/60" data-testid={`perm-row-${p.key}`}>
          <td className="sticky left-0 z-10 bg-card px-4 py-2">
            <div className="flex items-center gap-2 text-sm font-medium">{p.label}{p.write && <span className="eyebrow text-emerald-500">write</span>}</div>
            <div className="text-[11px] text-muted-foreground">{p.hint}</div>
          </td>
          {users.map((u) => {
            const on = u.permissions.includes(p.key);
            const locked = u.id === me.id && selfLocked?.includes(p.key);
            return (
              <td key={u.id} className={`border-l border-border px-3 py-2 text-center ${u.active ? "" : "opacity-50"}`}>
                {locked ? (
                  <Tooltip><TooltipTrigger asChild><span className="inline-grid h-5 w-5 place-items-center bg-primary/20 text-primary" data-testid={`perm-${u.id}-${p.key}-locked`}><Lock className="h-3 w-3" /></span></TooltipTrigger><TooltipContent>You can't remove this from yourself</TooltipContent></Tooltip>
                ) : (
                  <Checkbox checked={on} disabled={readOnly || !u.active} onCheckedChange={(v) => toggle(u, p.key, !!v)} className="h-5 w-5 rounded-none" data-testid={`perm-${u.id}-${p.key}`} />
                )}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
